import type { Lang, Translations } from '@/i18n/translations'
import { translations } from '@/i18n/translations'
import { createContext, useContext, useMemo, useState } from 'react'

interface LanguageContextValue {
  lang: Lang
  t: Translations
  toggleLang: () => void
}

const LanguageContext = createContext<LanguageContextValue | null>(null)

function getInitialLang(): Lang {
  if (typeof window === 'undefined')
    return 'fr'
  const stored = window.localStorage.getItem('mimi-factory-lang')
  return stored === 'en' ? 'en' : 'fr'
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLang] = useState<Lang>(getInitialLang)

  const toggleLang = () => {
    const next: Lang = lang === 'fr' ? 'en' : 'fr'
    setLang(next)
    if (typeof window !== 'undefined')
      window.localStorage.setItem('mimi-factory-lang', next)
  }

  const value = useMemo<LanguageContextValue>(() => ({
    lang,
    t: translations[lang],
    toggleLang,
  }), [lang])

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const ctx = useContext(LanguageContext)
  if (!ctx)
    throw new Error('useLanguage must be used within a LanguageProvider')
  return ctx
}
